import { LiveAnnouncer } from '@angular/cdk/a11y';
import {
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    Validators,
} from '@angular/forms';
import { AppStateFacade } from '@app-state';
import {
    UiExecutionSettingsComponent,
    UiModalComponent,
} from '@components';
import {
    CredentialStoreGenericType,
    CredentialStoreResourceType,
    ExecutionSettingsType,
    FeatureFlag,
    ICredentialStore,
    IExecutionSetting,
    IRobot,
    ISession,
    ISessionUpdate,
    ModalResultType,
    RobotHostingType,
    RobotState,
    RobotType,
} from '@models';
import { TranslateService } from '@ngx-translate/core';
import {
    CredentialStoresService,
    ResourceMapperService,
    RobotService,
    SettingService,
    SignalRService,
} from '@services';
import {
    ISuggestValue,
    ISuggestValueData,
} from '@uipath/angular/components/ui-suggest';
import { resolveUnionTypeByProperty } from '@utils/type.utils';
import UiValidators, {
    UI_ROBOT_DESCRIPTION_MAX_LENGTH,
    UI_ROBOT_NAME_MAX_LENGTH,
    UI_ROBOT_PASSWORD_MAX_LENGTH,
    UI_ROBOT_USERNAME_MAX_LENGTH,
    UI_ROBOT_USERNAME_REGEX,
} from '@validators';

import cloneDeep from 'lodash-es/cloneDeep';
import {
    BehaviorSubject,
    forkJoin,
    Subject,
} from 'rxjs';
import {
    filter,
    finalize,
    startWith,
    takeUntil,
} from 'rxjs/operators';

export enum RobotFormControl {
    hostingType = 'hostingType',
    machine = 'machine',
    name = 'name',
    type = 'type',
    description = 'description',
    robotCredentials = 'robotCredentials',
    username = 'userName',
    executionSettings = 'executionSettings',
    isExternalLicense = 'isExternalLicense',
}

export enum RobotCredentialsControl {
    credentialStore = 'credentialStore',
    credentialStoreConfig = 'credentialStoreConfig',
    isCredentialSmartCard = 'isCredentialSmartCard',
}

export enum CredentialStoreConfigControl {
    password = 'password',
    externalName = 'externalName',
}

export interface IRobotFormValue {
    hostingType: RobotHostingType;
    machine?: ISuggestValue[];
    name: string;
    type: ISuggestValue[];
    description: string;
    username: string;
    isExternalLicense: boolean;
    robotCredentials?: {
        credentialStore?: ISuggestValueData<ICredentialStore>[];
        credentialStoreConfig: {
            password: string;
        } | {
            externalName: string;
        } | {
            password: string;
            externalName: string;
        };
        isCredentialSmartCard?: boolean;
    };
    executionSettings?: {
        [key: string]: boolean | number | ISuggestValue[];
    };
}

const PASSWORD_CONTROL = (robot: IRobot) => new FormControl(
    robot.password,
    Validators.maxLength(UI_ROBOT_PASSWORD_MAX_LENGTH),
);

const EXTERNAL_NAME_CONTROL = (robot: IRobot) => new FormControl(robot.externalName);

export abstract class RobotModalBaseComponent extends UiModalComponent<IRobot>
    implements OnInit, OnDestroy {

    @ViewChild(UiExecutionSettingsComponent, { static: false })
    public set executionSettingsComponent(value: UiExecutionSettingsComponent) {
        if (
            !value ||
            !!this.robotForm.get(RobotFormControl.executionSettings)
        ) { return; }
        this._executionSettingsComponent = value;

        this.robotForm.addControl(
            RobotFormControl.executionSettings,
            value.formGroup,
        );
    }

    public executionSettingsValues = cloneDeep(this.context.data.executionSettings) || {};
    public robotForm!: FormGroup;
    public robotCredentialsForm!: FormGroup;
    public credentialStoreConfigControls!: {
        [key in CredentialStoreConfigControl]: FormControl
    };

    public robotTypeDropdownEntries: ISuggestValue[] = [];
    public createAnother = false;

    public credentialStoreDropdownEntries: ISuggestValueData<ICredentialStore>[] = [];

    public RobotFormControl = RobotFormControl;
    public RobotCredentialsControl = RobotCredentialsControl;
    public CredentialStoreConfigControl = CredentialStoreConfigControl;

    public RobotType = RobotType;
    public CredentialStoreGenericType = CredentialStoreGenericType;

    public submitting$ = new BehaviorSubject<boolean>(false);
    public loadingData$ = new BehaviorSubject<boolean>(true);
    public refocusField$ = new BehaviorSubject<boolean>(false);
    public executionSettingsConfig$ = new BehaviorSubject<IExecutionSetting[]>([]);
    public isRobotBusy$ = new BehaviorSubject<boolean>(false);

    protected _robotService = this._injector.get<RobotService>(RobotService);
    protected _credentialService = this._injector.get<CredentialStoresService>(CredentialStoresService);
    protected _translateService = this._injector.get<TranslateService>(TranslateService);
    protected _settingsService = this._injector.get<SettingService>(SettingService);
    protected _signalRService = this._injector.get<SignalRService>(SignalRService);
    protected _formBuilder = this._injector.get(FormBuilder);
    protected _liveAnnouncer = this._injector.get<LiveAnnouncer>(LiveAnnouncer);
    protected _appState = this._injector.get<AppStateFacade>(AppStateFacade);

    protected _destroyed$ = new Subject<void>();

    public abstract get hostingType(): RobotHostingType;
    public abstract get formTitle(): string;
    public abstract get formDescription(): string;

    public get robotTypes(): RobotType[] {
        return this._robotService.getRobotTypes(this.hostingType);
    }

    public get defaultRobotType(): RobotType {
        return this.context.data.type == null ? RobotType.Development : this.context.data.type;
    }

    public get selectedRobotType(): RobotType {
        const [selectedDropdownEntry] = this.robotForm.get(RobotFormControl.type)!.value;
        return selectedDropdownEntry.id;
    }

    public get selectedCredentialStore(): ICredentialStore {
        const [selectedDropdownEntry] = this.robotCredentialsForm.get(RobotCredentialsControl.credentialStore)!.value;
        return selectedDropdownEntry ? selectedDropdownEntry.data : null;
    }

    public get credentialStoreConfigForm(): FormGroup {
        return this.robotCredentialsForm.get(RobotCredentialsControl.credentialStoreConfig)! as FormGroup;
    }

    public get isSmartCardAuthFeatureEnabled() {
        return this._appState.settings.isFeatureEnabled(FeatureFlag.SmartCardAuth);
    }

    public get isSubmitDisabled() {
        return this.loadingData$.getValue() ||
            this.robotForm.invalid ||
            this.submitting$.getValue();
    }

    private _executionSettingsComponent?: UiExecutionSettingsComponent;

    ngOnInit() {
        if (this.context.isDuplicate) {
            this.context.data = {
                ...this.context.data,
                id: NaN,
                name: '',
                description: '',
                userName: '',
                password: '',
                externalName: '',
            };
        }

        this.robotForm = this._formBuilder.group(this._robotFormConfig);
        this.robotCredentialsForm = this._formBuilder.group(this._robotCredentialsFormConfig);

        this.robotForm.addControl(RobotFormControl.robotCredentials, this.robotCredentialsForm);

        this.credentialStoreConfigControls = {
            [CredentialStoreConfigControl.password]: PASSWORD_CONTROL(this.context.data),
            [CredentialStoreConfigControl.externalName]: EXTERNAL_NAME_CONTROL(this.context.data),
        };

        this.robotTypeDropdownEntries = this.robotTypes
            .map(this._robotTypeToDropdownEntry);

        this.robotForm
            .get(RobotFormControl.type)!
            .valueChanges
            .pipe(
                startWith([this._robotTypeToDropdownEntry(this.defaultRobotType)]),
                takeUntil(this._destroyed$),
            )
            .subscribe(
                (value: ISuggestValue[]) => {
                    const [selected] = value;
                    this._onRobotTypeChange(selected.id as RobotType);
                },
            );

        this.robotCredentialsForm
            .get(RobotCredentialsControl.credentialStore)!
            .valueChanges
            .pipe(
                takeUntil(this._destroyed$),
            )
            .subscribe(
                (value: ISuggestValueData<ICredentialStore>[]) => {
                    const [selected] = value;
                    selected ?
                        this._onCredentialStoreTypeChange(selected.data!.type.genericType, selected.data!.details.isReadOnly) :
                        this._onCredentialStoreTypeChange();
                },
            );

        forkJoin({
            settings: this._settingsService.getExecutionSettingsConfiguration(ExecutionSettingsType.Robot),
            credentialStores: this._credentialService.get(),
            defaultCredentialStoreId: this._credentialService.getDefaultStore(CredentialStoreResourceType.RobotCredential),
        })
            .pipe(
                finalize(() => this.loadingData$.next(false)),
            )
            .subscribe(
                ({ settings, credentialStores, defaultCredentialStoreId }) => {
                    this.executionSettingsConfig$.next(settings.configuration);

                    this.credentialStoreDropdownEntries = credentialStores.data.map(this._credentialStoreToDropdownEntry);

                    const defaultCredentialStore = credentialStores.data.find(store =>
                        store.id === (this.context.data.credentialStoreId || defaultCredentialStoreId),
                    );

                    this.robotCredentialsForm
                        .get(RobotCredentialsControl.credentialStore)!
                        .setValue(
                            defaultCredentialStore
                                ? [this._credentialStoreToDropdownEntry(defaultCredentialStore)]
                                : [],
                        );
                },
            );

        if (!this.context.isNewEntry) {
            this._subscribeToSessionUpdates();
        }

        this._announceFormInfo();
    }

    ngOnDestroy() {
        this._destroyed$.next();
        this._destroyed$.complete();
    }

    public resetPassword() {
        this.credentialStoreConfigControls[CredentialStoreConfigControl.password]
            .reset();
    }

    public onCredentialSmartCardChange() {
        if (!this.context.isNewEntry) {
            this.resetPassword();
        }
    }

    public onSubmit() {
        if (this.isSubmitDisabled) { return; }

        this.submitting$.next(true);

        const payload = this._formValueToPayload(this.robotForm.value);

        if (this.context.isNewEntry) {
            this._addRobot(payload);
        } else {
            this._updateRobot(payload);
        }
    }

    protected _addRobot(payload: any) {
        this.refocusField$.next(false);
        this._robotService.add(payload)
            .pipe(
                finalize(
                    () => {
                        this.submitting$.next(false);
                    },
                ),
            ).subscribe(
                response => {
                    if (!this.createAnother) {
                        this._dialogRef.close({
                            type: ModalResultType.Add,
                        });
                    } else {
                        this.customEvent.emit({
                            type: ModalResultType.Add,
                        });

                        this.robotForm.reset(this._getFormValueToCreateAnother(response));
                        this._executionSettingsComponent!.resetSettings();

                        this.refocusField$.next(true);
                    }
                },
            );
    }

    protected _updateRobot(payload: any) {
        const robotId = this.context.data.id;
        payload.id = robotId;

        this._robotService.update(robotId, payload)
            .pipe(
                finalize(
                    () => this.submitting$.next(false),
                ),
            )
            .subscribe(
                () => {
                    this._dialogRef.close({
                        type: ModalResultType.Update,
                        data: {
                            ...payload,
                            password: null,
                            executionSettings: null,
                        },
                    });
                },
            );
    }

    protected _robotTypeToDropdownEntry = (type: RobotType): ISuggestValue => ({
        id: type,
        text: this._translateService.instant(ResourceMapperService.robotType[RobotType[type]]),
    })

    protected _credentialStoreToDropdownEntry = (credentialStore: ICredentialStore): ISuggestValueData<ICredentialStore> => ({
        id: credentialStore.id,
        text: credentialStore.name,
        data: credentialStore,
    })

    protected _formValueToPayload({ robotCredentials, type, machine, ...rest }: IRobotFormValue) {
        const isExternalLicenseApplicable = [RobotType.Development, RobotType.StudioX].includes(this.selectedRobotType);
        rest[RobotFormControl.isExternalLicense] =
            isExternalLicenseApplicable
                ? !!rest[RobotFormControl.isExternalLicense]
                : false;

        const credentials = robotCredentials ? {
            credentialStoreId: this.selectedCredentialStore.id,
            ...(this.isSmartCardAuthFeatureEnabled ? {
                isCredentialSmartCard: robotCredentials.isCredentialSmartCard,
            } : {}),
            ...robotCredentials.credentialStoreConfig,
        } : {};

        return {
            type: this.selectedRobotType,
            ...credentials,
            ...rest,
        };
    }

    protected abstract _getFormValueToCreateAnother(robot: IRobot): {};

    private get _robotFormConfig() {
        return {
            [RobotFormControl.hostingType]: [
                this.hostingType,
            ],
            [RobotFormControl.name]: [
                this.context.data.name,
                Validators.compose([
                    UiValidators.required(),
                    Validators.maxLength(UI_ROBOT_NAME_MAX_LENGTH),
                ]),
            ],
            [RobotFormControl.type]: [
                [this._robotTypeToDropdownEntry(this.defaultRobotType)],
            ],
            [RobotFormControl.username]: [
                this.context.data.userName,
                Validators.compose([
                    UiValidators.required(),
                    Validators.maxLength(UI_ROBOT_USERNAME_MAX_LENGTH),
                    ...(this.hostingType === RobotHostingType.Floating ? [UiValidators.regex(UI_ROBOT_USERNAME_REGEX)] : []),
                ]),
            ],
            [RobotFormControl.description]: [
                this.context.data.description,
                Validators.maxLength(UI_ROBOT_DESCRIPTION_MAX_LENGTH),
            ],
            [RobotFormControl.isExternalLicense]: [
                !!this.context.data.isExternalLicense,
            ],
        };
    }

    private get _robotCredentialsFormConfig() {
        return {
            [RobotCredentialsControl.credentialStore]: [
                [],
                Validators.required,
            ],
            ...(this.isSmartCardAuthFeatureEnabled ? {
                [RobotCredentialsControl.isCredentialSmartCard]: [
                    this.context.data.isCredentialSmartCard,
                ],
            } : {}),
        };
    }

    private _onRobotTypeChange(type: RobotType) {
        type === RobotType.Attended ?
            this.robotForm.removeControl(RobotFormControl.robotCredentials) :
            this.robotForm.addControl(RobotFormControl.robotCredentials, this.robotCredentialsForm);
    }


    private _onCredentialStoreTypeChange(type?: CredentialStoreGenericType, isReadOnly = false) {
        this.robotCredentialsForm.removeControl(RobotCredentialsControl.credentialStoreConfig);

        this.robotCredentialsForm.addControl(RobotCredentialsControl.credentialStoreConfig, new FormGroup({}));

        if (type == null) {
            // mark the field visually according to its validators
            const credentialStoreControl = this.robotCredentialsForm.get(RobotCredentialsControl.credentialStore)!;
            credentialStoreControl.markAsTouched();
            return;
        }

        const credentialStoreConfig = this.robotCredentialsForm.get(RobotCredentialsControl.credentialStoreConfig)! as FormGroup;

        if (!isReadOnly) {
            credentialStoreConfig.addControl(
                CredentialStoreConfigControl.password,
                this.credentialStoreConfigControls.password,
            );
        }

        if (type === CredentialStoreGenericType.SecureStore) {
            credentialStoreConfig.addControl(
                CredentialStoreConfigControl.externalName,
                this.credentialStoreConfigControls.externalName,
            );
        }
    }

    private _announceFormInfo() {
        if (this.context.isNewEntry) {
            this._liveAnnouncer.announce(this.formTitle + ' ' + this.formDescription);
        }
    }

    private _subscribeToSessionUpdates() {
        this._signalRService.sessions$
            .pipe(
                startWith(this.context.additionalData.model as ISession),
                filter(
                    session => (
                        resolveUnionTypeByProperty<ISessionUpdate, ISession>(session, 'robotId') ?
                            session.robotId :
                            session.robot.id
                    ) === this.context.data.id,
                ),
                takeUntil(this._destroyed$),
            )
            .subscribe(
                session => this.isRobotBusy$.next(session.state === RobotState[RobotState.Busy]),
            );
    }
}
